
import { useState } from 'react'
import { maxUserNameLength, maxIntroductionLength } from '../../../../../shared-src/user'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import * as env from '@/env'
import { Input } from '@/components/ui/input'
import { useUserStore } from '@/state/user-store'
import { readSignupAttribution, clearSignupAttribution } from '@/signup-attribution'

interface IntroductionStepProps {
	email: string
	location: { lat: number; lng: number }
	name: string
	setName: (name: string) => void
	introduction: string
	setIntroduction: (introduction: string) => void
	setCurrentStep: (step: number) => void
}

export function IntroductionStepComponent(props: IntroductionStepProps) {
	const [isLoading, setIsLoading] = useState(false)

	const setUser = useUserStore(state => state.setUser)
	
	const minIntroductionLength = 0
	const minNameLength = 0
	const maxNameLength = maxUserNameLength

	const isValidIntroduction = (text: string): boolean => {
		return text.length >= minIntroductionLength && text.length <= maxIntroductionLength
	}

	const isValidName = (name: string): boolean => {
		return name.length >= minNameLength && name.length <= maxNameLength
	}

	const isContinueDisabled = !isValidIntroduction(props.introduction) || !isValidName(props.name)

	const handleSubmit = async (): Promise<void> => {
		setIsLoading(true)
		try {
			const attribution = readSignupAttribution()
			const response = await fetch(`${env.restBaseUrl}/api/v1/create-user`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					email: props.email,
					name: props.name,
					introduction: props.introduction,
					location: props.location,
					...(attribution && { attribution }),
				})
			})
			if (!response.ok) {
				throw new Error('Failed to create user')
			}

			const data = await response.json()
			setUser(data)
			clearSignupAttribution()

			// props.setCurrentStep(5)
			window.location.href = '/chat'
		} catch (error) {
			toast.error('Error creating user. Please try again.')
			console.error('Error creating user:', error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleBack = (): void => {
		props.setCurrentStep(3)
	}

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<label className="block text-sm font-normal text-gray-700">
					What's your name? Just your first name or nickname is fine. 
				</label>
				<Input
					value={props.name}
					onChange={(e) => props.setName(e.target.value)}
					placeholder="Your name"
					maxLength={maxNameLength}
				/>
			</div>
			<div className="space-y-2">
				<label className="block text-sm font-normal text-gray-700">
					What are your interests, hobbies, goals, values? What kind of people would you like to meet?
				</label>
				<label className="block text-sm font-normal text-gray-700">
					Later you'll be able to chat with our bot to refine all this, but give us something to start with. We'll use it to form small chat groups with a few others in your area who share similar interests.
				</label>
				<Textarea
					value={props.introduction}
					onChange={(e) => props.setIntroduction(e.target.value)}
					rows={6}
					maxLength={maxIntroductionLength}
				/>
				{/* <div className="text-sm text-gray-500">
					{props.introduction.length}/{maxLength} characters (minimum {minLength})
				</div> */}
			</div>
			<div className="space-y-2">
				<div className="flex justify-between">
					<Button onClick={handleBack} className="bg-red-500 hover:bg-red-600 text-white flex items-center justify-center w-24">
						<ChevronLeft className="mr-2 h-4 w-4" />
						Back
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isContinueDisabled || isLoading}
						className="bg-green-500 hover:bg-green-600 text-white flex items-center justify-center w-32"
					>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							</>
						) : (
							<>
								Let's go!
								<ChevronRight className="ml-2 h-4 w-4" />
							</>
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}
